import React from 'react';
import { X, Network, FileText } from 'lucide-react';

function TaxonomyNodeDetailModal({ isOpen, onClose, nodeData, loading }) {
    if (!isOpen) return null;

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]">
                <div className="bg-white p-8 rounded-xl shadow-2xl flex flex-col items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading Profile...</p>
                </div>
            </div>
        );
    }

    if (!nodeData) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden animate-slide-up">
                
                {/* Header */}
                <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-white">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        {nodeData.type || 'Taxonomy Node'} Profile
                    </span>
                    <button 
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                    
                    {/* Title Section */}
                    <div>
                        <div className="flex gap-2 mb-3">
                            <span className="px-3 py-1 bg-purple-50 text-purple-600 text-xs font-bold rounded-full uppercase tracking-wide flex items-center gap-1">
                                <Network size={12} />
                                Node
                            </span>
                        </div>
                        
                        <h2 className="text-2xl font-bold text-gray-900 leading-tight mb-2">
                            {nodeData.name}
                        </h2>
                    </div>

                    {/* Description Section */}
                    {nodeData.description && (<div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText size={14} className="text-blue-600" />
                            Description
                        </h3>
                        <p className="text-sm leading-relaxed text-gray-700">
                            {nodeData.description || "No description available for this node."}
                        </p>
                    </div>)} 

                    {nodeData.input && (<div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText size={14} className="text-blue-600" />
                            Input
                        </h3>
                        <p className="text-sm leading-relaxed text-gray-700">
                            {nodeData.input || "No input information available for this node."}
                        </p>
                    </div>)}    

                    {nodeData.output && (<div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText size={14} className="text-blue-600" />
                            Output
                        </h3>
                        <p className="text-sm leading-relaxed text-gray-700">
                            {nodeData.output || "No output information available for this node."}
                        </p>
                    </div>)}

                    {nodeData.pros && (<div className="bg-gray-50 p-5 rounded-xl border border-gray-100">
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText size={14} className="text-blue-600" />
                            Pros
                        </h3>
                        <p className="text-sm leading-relaxed text-gray-700">
                            {nodeData.pros || "No pros information available for this node."}
                        </p>
                    </div>)}  

                    {nodeData.cons && (<div className="bg-gray-50 p-5 rounded-xl border border-gray-100">     
                        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText size={14} className="text-blue-600" />
                            Cons
                        </h3>
                        <p className="text-sm leading-relaxed text-gray-700">
                            {nodeData.cons || "No cons information available for this node."}
                        </p>
                    </div>)}     

                    
                    {/* Metadata (Optional/Placeholder) */}
                    <div className="flex gap-4 text-xs text-gray-400 border-t border-gray-100 pt-4">
                        <span>ID: {nodeData.id}</span>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default TaxonomyNodeDetailModal;